/**
 * Client wrappers for Photo Studio AI callables.
 */

import { getFunctions, httpsCallable, type FunctionsError } from 'firebase/functions';
import { getClientApp, getClientAuth } from '@canvas/firebase-config/client';
import { uploadProjectImage } from '@/lib/uploadAsset';

export interface AiCredits {
  remaining: number;
  limit: number;
  reelRemaining?: number;
  reelLimit?: number;
}

export interface AiCreditsSnapshot {
  credits: AiCredits;
  costs: Record<string, number>;
  isPro: boolean;
  planLimit: number;
  reelLimit: number;
}

/** Must match Cloud Functions region (Singapore / SEA). */
const FUNCTIONS_REGION = 'asia-southeast1';

function functions() {
  return getFunctions(getClientApp(), FUNCTIONS_REGION);
}

function aiErrorMessage(err: unknown): string {
  const fe = err as FunctionsError;
  if (fe?.code === 'functions/resource-exhausted') {
    return fe.message || 'AI credits exhausted. Upgrade to Pro for more.';
  }
  if (fe?.code === 'functions/unauthenticated') {
    return 'Sign in to use cloud AI features.';
  }
  if (fe?.code === 'functions/permission-denied') {
    return fe.message || 'This feature requires Pro.';
  }
  if (
    fe?.code === 'functions/deadline-exceeded' ||
    fe?.code === 'functions/unavailable' ||
    /deadline|timeout/i.test(fe?.message || '')
  ) {
    return 'AI motion timed out. Wait a moment and retry, or use free Preview motion.';
  }
  if (err instanceof Error) return err.message;
  return 'AI request failed';
}

export async function fetchAiCredits(): Promise<AiCreditsSnapshot | null> {
  const auth = getClientAuth();
  if (!auth.currentUser) return null;
  // Ensure auth token is ready before calling SEA region functions
  await auth.currentUser.getIdToken();
  const callable = httpsCallable<unknown, AiCreditsSnapshot>(functions(), 'getAiCredits');
  const res = await callable({});
  return res.data;
}

/**
 * Ensure an image URL is http(s) for cloud AI.
 * Uploads blob/data URLs to Storage when authenticated.
 */
export async function ensureHttpImageUrl(
  url: string,
  projectId: string,
  filename = 'photo-studio.png'
): Promise<string> {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (!getClientAuth().currentUser) {
    throw new Error('Sign in and upload to Storage before using cloud AI.');
  }

  const res = await fetch(url);
  const blob = await res.blob();
  const file = new File([blob], filename, { type: blob.type || 'image/png' });
  const uploaded = await uploadProjectImage({ file, projectId });
  if (uploaded.source !== 'storage' || !uploaded.url.startsWith('http')) {
    throw new Error('Could not upload image to Storage for AI processing.');
  }
  return uploaded.url;
}

async function callAi<TReq extends object, TRes>(
  name: string,
  data: TReq,
  opts?: { timeoutMs?: number }
): Promise<TRes> {
  try {
    const callable = httpsCallable<TReq, TRes>(functions(), name, {
      timeout: opts?.timeoutMs ?? 70000,
    });
    const res = await callable(data);
    return res.data;
  } catch (err) {
    throw new Error(aiErrorMessage(err));
  }
}

export async function callEnhancePortrait(opts: {
  imageUrl: string;
  strength?: number;
}): Promise<{ processedImageUrl: string; credits: AiCredits; mode: string }> {
  return callAi('enhancePortrait', opts);
}

export async function callAdjustSkinTone(opts: {
  imageUrl: string;
  amount: number;
}): Promise<{ processedImageUrl: string; credits: AiCredits }> {
  return callAi('adjustSkinTone', opts);
}

export async function callSwapOutfit(opts: {
  imageUrl: string;
  presetId?: string;
  prompt?: string;
}): Promise<{ processedImageUrl: string; credits: AiCredits; mode: string }> {
  return callAi('swapOutfit', opts);
}

export async function callReplaceBackground(opts: {
  imageUrl: string;
  alreadyCutout?: boolean;
  background?: {
    type: 'solid' | 'gradient' | 'generative';
    color?: string;
    color2?: string;
    prompt?: string;
  };
  width?: number;
  height?: number;
}): Promise<{ processedImageUrl: string; credits: AiCredits; mode: string }> {
  return callAi('replaceBackground', opts);
}

export async function callCreateReel(opts: {
  imageUrl: string;
  projectId?: string;
  platformId?: string;
  durationSec?: number;
  width?: number;
  height?: number;
}): Promise<{
  mode: 'vendor' | 'kenburns';
  processedVideoUrl?: string;
  storagePath?: string;
  reelId?: string;
  imageUrl: string;
  durationSec: number;
  width: number;
  height: number;
  credits: AiCredits;
  vendorError?: string;
}> {
  // AI motion often takes 1–4 minutes; default callable timeout (~70s) is too short.
  return callAi('createReel', opts, { timeoutMs: 320000 });
}

export async function callRemoveBackground(opts: {
  imageUrl: string;
}): Promise<{ processedImageUrl: string; credits?: AiCredits }> {
  return callAi('removeBackground', opts);
}
